import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Plus, 
    Search, 
    Pencil, 
    Trash2, 
    Gem, 
    Sparkles, 
    ArrowUpCircle, 
    Trophy, 
    BookOpen,
    FileText,
    Users,
    Flag,
    ChevronRight,
    Settings,
    Lock,
    Unlock,
    Database,
    Watch,
    History,
    Crown,
    Zap,
    Shield,
    Construction,
    Menu,
    X,
    Ticket
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useSiteLock } from '../context/SiteLockContext';
import { useLanguage } from '../context/LanguageContext';
import { entriesApi, patchNotesApi, teamApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { CreateEditModal } from '../components/admin/CreateEditModal';
import { DeleteConfirmModal } from '../components/admin/DeleteConfirmModal';
import { PatchNoteModal } from '../components/admin/PatchNoteModal';
import { TeamMemberModal } from '../components/admin/TeamMemberModal';
import { IslandManager } from '../components/admin/IslandManager';
import { GachaManager } from '../components/admin/GachaManager';
import { UpgradeManager } from '../components/admin/UpgradeManager';
import { AchievementManager } from '../components/admin/AchievementManager';
import { DataBackupManager } from '../components/admin/DataBackupManager';
import { ChangeHistoryManager } from '../components/admin/ChangeHistoryManager';
import { MetaBuildManager } from '../components/admin/MetaBuildManager';
import { WhitelistManager } from '../components/admin/WhitelistManager';
import { CodesManager } from '../components/admin/CodesManager';
import { LockModeManager } from '../components/admin/LockModeManager';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const RARITY_COLORS = {
    Uncommon: 'text-green-400',
    Rare: 'text-blue-400',
    Epic: 'text-purple-400',
    Legendary: 'text-amber-400',
    Mythical: 'text-red-400',
    Monarch: 'text-pink-400',
    Limited: 'text-cyan-400',
};

const getNavSections = (t) => [
    {
        title: t('wikiContent'),
        items: [
            { id: 'relics', label: 'Relics', icon: Gem },
            { id: 'accessories', label: 'Accessories', icon: Watch },
            { id: 'skills', label: 'Skills', icon: Zap },
            { id: 'gachas', label: 'Gachas', icon: Sparkles },
            { id: 'upgrades', label: 'Upgrades', icon: ArrowUpCircle },
            { id: 'achievements', label: 'Achievements', icon: Trophy },
            { id: 'tutorials', label: 'Tutorials', icon: BookOpen },
        ]
    },
    {
        title: t('management'),
        items: [
            { id: 'lockmode', label: t('lockMode'), icon: Construction },
            { id: 'whitelist', label: t('whitelist'), icon: Shield },
            { id: 'metabuilds', label: t('metaBuilds'), icon: Crown },
            { id: 'databackup', label: t('dataBackup'), icon: Database },
            { id: 'changehistory', label: t('changeHistory'), icon: History },
            { id: 'patchnotes', label: t('patchNotes'), icon: FileText },
            { id: 'team', label: t('team'), icon: Users },
            { id: 'reports', label: t('reports'), icon: Flag },
	    { id: 'codes', label: t('codes'), icon: Ticket },
        ]
    }
];

const WIKI_CATEGORIES = ['relics', 'accessories', 'skills', 'tutorials'];

// Admin description helper
const getAdminDesc = (t, section) => {
    const map = {
        relics: t('adminDescRelics'),
        accessories: t('adminDescAccessories'),
        skills: t('adminDescSkills'),
        gachas: t('adminDescGachas'),
        upgrades: t('adminDescUpgrades'),
        achievements: t('adminDescAchievements'),
        tutorials: t('adminDescTutorials'),
        whitelist: t('adminDescWhitelist'),
        metabuilds: t('adminDescMetaBuilds'),
        databackup: t('adminDescDataBackup'),
        changehistory: t('adminDescChangeHistory'),
        patchnotes: t('adminDescPatchNotes'),
        team: t('adminDescTeam'),
        reports: t('adminDescReports'),
        codes: t('adminDescCodes'),
        lockmode: t('adminDescLockMode'),
    };
    return map[section] || '';
};

export default function AdminPage() {
    const { isAdmin, user } = useAuth();
    const navigate = useNavigate();
    const { t } = useLanguage();
    const [activeSection, setActiveSection] = useState('relics');
    const [entries, setEntries] = useState({});
    const [patchNotes, setPatchNotes] = useState([]);
    const [teamMembers, setTeamMembers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    
    const [selectedIsland, setSelectedIsland] = useState(null);
    
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedEntry, setSelectedEntry] = useState(null);
    
    const [patchModalOpen, setPatchModalOpen] = useState(false);
    const [editPatchModalOpen, setEditPatchModalOpen] = useState(false);
    const [deletePatchModalOpen, setDeletePatchModalOpen] = useState(false);
    const [selectedPatch, setSelectedPatch] = useState(null);
    
    const [teamModalOpen, setTeamModalOpen] = useState(false);
    const [editTeamModalOpen, setEditTeamModalOpen] = useState(false);
    const [deleteTeamModalOpen, setDeleteTeamModalOpen] = useState(false);
    const [selectedMember, setSelectedMember] = useState(null);
    
    const { lockedPages, togglePageLock, isPageLockedForAdmin } = useSiteLock();

    useEffect(() => {
        if (!isAdmin) {
            navigate('/');
            return;
        }
        loadAllData();
    }, [isAdmin, navigate]);

    const handleTogglePageLock = async (page) => {
        try {
            const newStatus = await togglePageLock(page);
            toast.success(`${page} ${newStatus ? t('locked') : t('unlocked')}`);
        } catch (error) {
            toast.error(t('lockError'));
        }
    };

    const loadAllData = async () => {
        setLoading(true);
        try {
            const allEntries = {};
            for (const category of WIKI_CATEGORIES) {
                const response = await entriesApi.getAll(category);
                allEntries[category] = Array.isArray(response.data) ? response.data : (response.data.items || []);
            }
            setEntries(allEntries);

            const patchRes = await patchNotesApi.getAll();
            setPatchNotes(Array.isArray(patchRes.data) ? patchRes.data : (patchRes.data.items || patchRes.data));

            const teamRes = await teamApi.getAll();
            setTeamMembers(Array.isArray(teamRes.data) ? teamRes.data : (teamRes.data.items || teamRes.data));
        } catch (error) {
            console.error('Failed to load data:', error);
            toast.error(t('loadError'));
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async (data) => {
        try {
            await entriesApi.create({ ...data, category: activeSection }, user?.discord_id, user?.username);
            toast.success(t('entryCreated'));
            loadAllData();
            setCreateModalOpen(false);
        } catch (error) {
            toast.error(t('createError'));
        }
    };

    const handleEdit = async (data) => {
        if (!selectedEntry) return;
        try {
            await entriesApi.update(selectedEntry.id, { ...data, category: data.category || activeSection }, user?.discord_id, user?.username);
            toast.success(t('entryUpdated'));
            loadAllData();
            setEditModalOpen(false);
            setSelectedEntry(null);
        } catch (error) {
            toast.error(t('updateError'));
        }
    };

    const handleDelete = async () => {
        if (!selectedEntry) return;
        try {
            await entriesApi.delete(selectedEntry.id, user?.discord_id, user?.username);
            toast.success(t('entryDeleted'));
            loadAllData();
            setDeleteModalOpen(false);
            setSelectedEntry(null);
        } catch (error) {
            toast.error(t('deleteError'));
        }
    };

    const handleCreatePatch = async (data) => {
        try {
            await patchNotesApi.create(data, user?.discord_id, user?.username);
            toast.success(t('patchNoteCreated'));
            loadAllData();
            setPatchModalOpen(false);
        } catch (error) {
            toast.error(t('createError'));
        }
    };

    const handleEditPatch = async (data) => {
        if (!selectedPatch) return;
        try {
            await patchNotesApi.update(selectedPatch.id, data, user?.discord_id, user?.username);
            toast.success(t('patchNoteUpdated'));
            loadAllData();
            setEditPatchModalOpen(false);
            setSelectedPatch(null);
        } catch (error) {
            toast.error(t('updateError'));
        }
    };

    const handleDeletePatch = async () => {
        if (!selectedPatch) return;
        try {
            await patchNotesApi.delete(selectedPatch.id, user?.discord_id, user?.username);
            toast.success(t('patchNoteDeleted'));
            loadAllData();
            setDeletePatchModalOpen(false);
            setSelectedPatch(null);
        } catch (error) {
            toast.error(t('deleteError'));
        }
    };

    const handleCreateMember = async (data) => {
        try {
            await teamApi.create(data, user?.discord_id, user?.username);
            toast.success(t('teamMemberAdded'));
            loadAllData();
            setTeamModalOpen(false);
        } catch (error) {
            toast.error(t('addError'));
        }
    };

    const handleEditMember = async (data) => {
        if (!selectedMember) return;
        try {
            await teamApi.update(selectedMember.id, data, user?.discord_id, user?.username);
            toast.success(t('teamMemberUpdated'));
            loadAllData();
            setEditTeamModalOpen(false);
            setSelectedMember(null);
        } catch (error) {
            toast.error(t('updateError'));
        }
    };

    const handleDeleteMember = async () => {
        if (!selectedMember) return;
        try {
            await teamApi.delete(selectedMember.id, user?.discord_id, user?.username);
            toast.success(t('teamMemberRemoved'));
            loadAllData();
            setDeleteTeamModalOpen(false);
            setSelectedMember(null);
        } catch (error) {
            toast.error(t('removeError'));
        }
    };

    const getFilteredItems = () => {
        if (activeSection === 'patchnotes') {
            const notes = Array.isArray(patchNotes) ? patchNotes : [];
            return notes.filter(note =>
                note.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                note.content?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        if (activeSection === 'team') {
            const members = Array.isArray(teamMembers) ? teamMembers : [];
            return members.filter(member =>
                member.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                member.role?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        const currentEntries = entries[activeSection] || [];
        const entriesArray = Array.isArray(currentEntries) ? currentEntries : [];
        return entriesArray.filter(entry =>
            entry.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            entry.description?.toLowerCase().includes(searchTerm.toLowerCase())
        );
    };

    const filteredItems = getFilteredItems();
    const isWikiSection = WIKI_CATEGORIES.includes(activeSection);

    const renderContent = () => {
        if (activeSection === 'lockmode') {
            return <LockModeManager />;
        }

        if (activeSection === 'gachas') {
            return (
                <div className="space-y-6">
                    <IslandManager 
                        onSelectIsland={setSelectedIsland}
                        selectedIslandId={selectedIsland?.id}
                        type="gacha"
                    />
                    <div className="border-t border-white/10 pt-6">
                        <GachaManager island={selectedIsland} />
                    </div>
                </div>
            );
        }

        if (activeSection === 'upgrades') {
            return (
                <div className="space-y-6">
                    <IslandManager 
                        onSelectIsland={setSelectedIsland}
                        selectedIslandId={selectedIsland?.id}
                        type="upgrade"
                    />
                    <div className="border-t border-white/10 pt-6">
                        <UpgradeManager island={selectedIsland} />
                    </div>
                </div>
            );
        }

        if (activeSection === 'achievements') {
            return <AchievementManager />;
        }

        if (activeSection === 'databackup') {
            return <DataBackupManager />;
        }

        if (activeSection === 'changehistory') {
            return <ChangeHistoryManager />;
        }

        if (activeSection === 'whitelist') {
            return <WhitelistManager />;
        }

        if (activeSection === 'codes') {
            return <CodesManager />;
        }

        if (activeSection === 'metabuilds') {
            return <MetaBuildManager />;
        }


        if (activeSection === 'reports') {
            return (
                <div className="p-8 text-center">
                    <Flag size={48} className="mx-auto mb-4 text-gray-600" />
                    <p className="text-gray-400 mb-4">{t('reportsOnSeparatePage')}</p>
                    <Button
                        onClick={() => navigate('/reports')}
                        className="bg-cyan-500 hover:bg-cyan-400 text-black"
                    >
                        {t('goToReports')}
                    </Button>
                </div>
            );
        }

        if (activeSection === 'patchnotes') {
            return (
                <div className="space-y-6">
                    <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                        <div className="relative flex-1 w-full sm:max-w-md">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                            <Input
                                data-testid="admin-search-input"
                                type="text"
                                placeholder={t('searchPatchNotes')}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-11 bg-black/40 border-white/10"
                            />
                        </div>
                        <Button
                            data-testid="create-patch-button"
                            onClick={() => setPatchModalOpen(true)}
                            className="bg-amber-500 hover:bg-amber-400 text-black font-semibold shrink-0 w-full sm:w-auto"
                        >
                            <Plus size={18} className="mr-2" />
                            {t('newPatchNote')}
                        </Button>
                    </div>

                    <div className="rounded-xl border border-white/10 overflow-hidden">
                        {loading ? (
                            <div className="p-8 text-center text-gray-500">{t('loading')}</div>
                        ) : filteredItems.length === 0 ? (
                            <div className="p-8 text-center text-gray-500">{t('noPatchNotesFound')}</div>
                        ) : (
                            <div className="divide-y divide-white/5">
                                {filteredItems.map((note) => (
                                    <div
                                        key={note.id}
                                        data-testid={`admin-patch-${note.id}`}
                                        className="flex items-center justify-between p-4 bg-card/30 hover:bg-white/5 transition-colors"
                                    >
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <h3 className="font-['Rajdhani'] font-semibold text-white">{note.title}</h3>
                                                <span className="px-2 py-0.5 rounded text-xs font-mono bg-amber-500/10 text-amber-400 border border-amber-500/20">
                                                    v{note.version}
                                                </span>
                                            </div>
                                            <p className="text-sm text-gray-500 truncate">{note.content?.substring(0, 100)}...</p>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0 ml-4">
                                            <Button variant="ghost" size="icon" data-testid={`edit-patch-${note.id}`}
                                                onClick={() => { setSelectedPatch(note); setEditPatchModalOpen(true); }}
                                                className="text-gray-400 hover:text-amber-400 hover:bg-amber-500/10"
                                            ><Pencil size={18} /></Button>
                                            <Button variant="ghost" size="icon" data-testid={`delete-patch-${note.id}`}
                                                onClick={() => { setSelectedPatch(note); setDeletePatchModalOpen(true); }}
                                                className="text-gray-400 hover:text-red-400 hover:bg-red-500/10"
                                            ><Trash2 size={18} /></Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        if (activeSection === 'team') {
            return (
                <div className="space-y-6">
                    <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                        <div className="relative flex-1 w-full sm:max-w-md">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                            <Input
                                data-testid="admin-search-input"
                                type="text"
                                placeholder={t('searchTeamMembers')}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-11 bg-black/40 border-white/10"
                            />
                        </div>
                        <Button
                            data-testid="create-member-button"
                            onClick={() => setTeamModalOpen(true)}
                            className="bg-purple-500 hover:bg-purple-400 text-white font-semibold shrink-0 w-full sm:w-auto"
                        >
                            <Plus size={18} className="mr-2" />
                            {t('addTeamMember')}
                        </Button>
                    </div>

                    <div className="rounded-xl border border-white/10 overflow-hidden">
                        {loading ? (
                            <div className="p-8 text-center text-gray-500">{t('loading')}</div>
                        ) : filteredItems.length === 0 ? (
                            <div className="p-8 text-center text-gray-500">{t('noTeamMembersFound')}</div>
                        ) : (
                            <div className="divide-y divide-white/5">
                                {filteredItems.map((member) => (
                                    <div key={member.id} data-testid={`admin-member-${member.id}`}
                                        className="flex items-center justify-between p-4 bg-card/30 hover:bg-white/5 transition-colors"
                                    >
                                        <div className="flex items-center gap-4 min-w-0">
                                            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full overflow-hidden border border-white/10 shrink-0">
                                                {member.avatar_url ? (
                                                    <img src={member.avatar_url} alt={member.name} className="w-full h-full object-cover" />
                                                ) : (
                                                    <div className="w-full h-full bg-purple-500/20 flex items-center justify-center">
                                                        <span className="text-lg font-bold text-purple-400">{member.name?.charAt(0).toUpperCase()}</span>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="min-w-0">
                                                <h3 className="font-['Rajdhani'] font-semibold text-white">{member.name}</h3>
                                                {member.alias && <p className="text-xs text-white/60">{member.alias}</p>}
                                                <p className="text-sm text-gray-500">{member.role}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0 ml-4">
                                            <Button variant="ghost" size="icon" data-testid={`edit-member-${member.id}`}
                                                onClick={() => { setSelectedMember(member); setEditTeamModalOpen(true); }}
                                                className="text-gray-400 hover:text-purple-400 hover:bg-purple-500/10"
                                            ><Pencil size={18} /></Button>
                                            <Button variant="ghost" size="icon" data-testid={`delete-member-${member.id}`}
                                                onClick={() => { setSelectedMember(member); setDeleteTeamModalOpen(true); }}
                                                className="text-gray-400 hover:text-red-400 hover:bg-red-500/10"
                                            ><Trash2 size={18} /></Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        if (isWikiSection) {
            return (
                <div className="space-y-6">
                    <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                        <div className="relative flex-1 w-full sm:max-w-md">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                            <Input
                                data-testid="admin-search-input"
                                type="text"
                                placeholder={t('searchEntries')}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-11 bg-black/40 border-white/10"
                            />
                        </div>
                        <div className="flex gap-2 w-full sm:w-auto">
                            <Button
                                data-testid="lock-page-button"
                                onClick={() => handleTogglePageLock(activeSection)}
                                className={cn(
                                    "font-semibold flex-1 sm:flex-initial",
                                    isPageLockedForAdmin(activeSection) 
                                        ? "bg-red-500 hover:bg-red-400 text-white" 
                                        : "bg-gray-600 hover:bg-gray-500 text-white"
                                )}
                            >
                                {isPageLockedForAdmin(activeSection) ? <Lock size={18} className="mr-2" /> : <Unlock size={18} className="mr-2" />}
                                {isPageLockedForAdmin(activeSection) ? t('unlock') : t('lock')}
                            </Button>
                            <Button
                                data-testid="create-entry-button"
                                onClick={() => setCreateModalOpen(true)}
                                className="bg-cyan-500 hover:bg-cyan-400 text-black font-semibold flex-1 sm:flex-initial"
                            >
                                <Plus size={18} className="mr-2" />
                                {t('newEntry')}
                            </Button>
                        </div>
                    </div>

                    <div className="rounded-xl border border-white/10 overflow-hidden">
                        {loading ? (
                            <div className="p-8 text-center text-gray-500">{t('loading')}</div>
                        ) : filteredItems.length === 0 ? (
                            <div className="p-8 text-center text-gray-500">
                                {t('noEntriesIn')} {activeSection} {t('found')}
                            </div>
                        ) : (
                            <div className="divide-y divide-white/5">
                                {filteredItems.map((entry) => (
                                    <div key={entry.id} data-testid={`admin-entry-${entry.id}`}
                                        className="flex items-center justify-between p-4 bg-card/30 hover:bg-white/5 transition-colors"
                                    >
                                        <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                                            {entry.image_url && (
                                                <img src={entry.image_url} alt={entry.title}
                                                    className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg object-cover shrink-0" />
                                            )}
                                            <div className="min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <h3 className="font-['Rajdhani'] font-semibold text-white truncate">{entry.title}</h3>
                                                    {entry.rarity && (
                                                        <span className={cn("text-xs font-mono", RARITY_COLORS[entry.rarity])}>
                                                            [{entry.rarity}]
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-sm text-gray-500 truncate">
                                                    {entry.description || t('noDescription')}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1 sm:gap-2 shrink-0 ml-2">
                                            <Button variant="ghost" size="icon" data-testid={`edit-${entry.id}`}
                                                onClick={() => { setSelectedEntry(entry); setEditModalOpen(true); }}
                                                className="text-gray-400 hover:text-cyan-400 hover:bg-cyan-500/10"
                                            ><Pencil size={18} /></Button>
                                            <Button variant="ghost" size="icon" data-testid={`delete-${entry.id}`}
                                                onClick={() => { setSelectedEntry(entry); setDeleteModalOpen(true); }}
                                                className="text-gray-400 hover:text-red-400 hover:bg-red-500/10"
                                            ><Trash2 size={18} /></Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        return null;
    };

    const NAV_SECTIONS = getNavSections(t);

    return (
        <div className="flex flex-col lg:flex-row gap-6" data-testid="admin-page">
            {/* Mobile Nav Toggle */}
            <div className="lg:hidden flex items-center justify-between mb-2">
                <h1 className="text-xl font-bold font-['Rajdhani'] tracking-wider text-white dark:text-white light:text-gray-900 flex items-center gap-2">
                    <Settings size={20} className="text-cyan-400" />
                    {t('adminPanel')}
                </h1>
                <button
                    onClick={() => setMobileNavOpen(!mobileNavOpen)}
                    data-testid="admin-mobile-nav-toggle"
                    className="p-2 rounded-lg bg-white/5 border border-white/10 text-gray-400 hover:text-white"
                >
                    {mobileNavOpen ? <X size={20} /> : <Menu size={20} />}
                </button>
            </div>

            {/* Sidebar Navigation */}
            <aside className={cn(
                "w-full lg:w-56 shrink-0",
                mobileNavOpen ? "block" : "hidden lg:block"
            )}>
                <div className="lg:sticky lg:top-4 space-y-6">
                    <div className="hidden lg:block space-y-1">
                        <h1 className="text-xl font-bold font-['Rajdhani'] tracking-wider text-white dark:text-white light:text-gray-900 flex items-center gap-2">
                            <Settings size={20} className="text-cyan-400" />
                            {t('adminPanel')}
                        </h1>
                    </div>

                    <nav className="space-y-6">
                        {NAV_SECTIONS.map((section) => (
                            <div key={section.title} className="space-y-1">
                                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-3">
                                    {section.title}
                                </h3>
                                <div className="space-y-0.5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-1 gap-1 lg:gap-0">
                                    {section.items.map((item) => {
                                        const Icon = item.icon;
                                        const isActive = activeSection === item.id;
                                        return (
                                            <button
                                                key={item.id}
                                                onClick={() => {
                                                    setActiveSection(item.id);
                                                    setSearchTerm('');
                                                    setMobileNavOpen(false);
                                                    if (item.id === 'gachas' || item.id === 'upgrades') {
                                                        setSelectedIsland(null);
                                                    }
                                                }}
                                                data-testid={`nav-${item.id}`}
                                                className={cn(
                                                    "w-full flex items-center gap-2 lg:gap-3 px-3 py-2 rounded-lg text-sm transition-colors",
                                                    isActive
                                                        ? "bg-cyan-500/10 text-cyan-400 border border-cyan-500/30"
                                                        : "text-gray-400 hover:text-white hover:bg-white/5 dark:hover:bg-white/5 light:hover:bg-gray-100"
                                                )}
                                            >
                                                <Icon size={16} />
                                                <span className="flex-1 text-left truncate">{item.label}</span>
                                                {isActive && <ChevronRight size={14} className="hidden lg:block" />}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </nav>
                </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 min-w-0">
                <div className="mb-6">
                    <h2 className="text-2xl font-bold font-['Rajdhani'] text-white dark:text-white light:text-gray-900 capitalize">
                        {activeSection === 'patchnotes' ? t('patchNotes') :
			 activeSection === 'codes' ? t('codes') :
                         activeSection === 'lockmode' ? t('lockMode') :
                         activeSection === 'metabuilds' ? t('metaBuilds') :
                         activeSection === 'databackup' ? t('dataBackup') :
                         activeSection === 'changehistory' ? t('changeHistory') :
                         activeSection === 'whitelist' ? t('whitelist') :
                         activeSection}
                    </h2>
                    <p className="text-gray-500 dark:text-gray-500 light:text-gray-600 text-sm">
                        {getAdminDesc(t, activeSection)}
                    </p>
                </div>

                {renderContent()}
            </main>

            {/* Modals */}
            <CreateEditModal open={createModalOpen} onOpenChange={setCreateModalOpen} onSubmit={handleCreate} category={activeSection} mode="create" />
            <CreateEditModal open={editModalOpen} onOpenChange={setEditModalOpen} onSubmit={handleEdit} category={activeSection} entry={selectedEntry} mode="edit" />
            <DeleteConfirmModal open={deleteModalOpen} onOpenChange={setDeleteModalOpen} onConfirm={handleDelete} entryTitle={selectedEntry?.title} />
            <PatchNoteModal open={patchModalOpen} onOpenChange={setPatchModalOpen} onSubmit={handleCreatePatch} mode="create" />
            <PatchNoteModal open={editPatchModalOpen} onOpenChange={setEditPatchModalOpen} onSubmit={handleEditPatch} note={selectedPatch} mode="edit" />
            <DeleteConfirmModal open={deletePatchModalOpen} onOpenChange={setDeletePatchModalOpen} onConfirm={handleDeletePatch} entryTitle={selectedPatch?.title} />
            <TeamMemberModal open={teamModalOpen} onOpenChange={setTeamModalOpen} onSubmit={handleCreateMember} mode="create" />
            <TeamMemberModal open={editTeamModalOpen} onOpenChange={setEditTeamModalOpen} onSubmit={handleEditMember} member={selectedMember} mode="edit" />
            <DeleteConfirmModal open={deleteTeamModalOpen} onOpenChange={setDeleteTeamModalOpen} onConfirm={handleDeleteMember} entryTitle={selectedMember?.name} />
        </div>
    );
}
